import {
  HiOutlineChartBar,
  HiOutlineCreditCard,
  HiOutlineUserCircle,
} from 'react-icons/hi'

const sections = [
  {
    icon: HiOutlineChartBar,
    title: 'Check your usage metrics',
    intro:
      'See how much the platform is being used: activity, number of users and space taken by your data.',
    steps: [
      'Sign in at supabase.com/dashboard.',
      'Select your organization, then the Kleezy project.',
      '"Project Overview" shows the status of the project (it should say "Healthy") and the recent activity of the database, the sign-ins and the file storage.',
      'In the left menu, open "Observability" for detailed charts over a longer period, such as the number of requests and the database usage.',
      'In the left menu, open "Authentication", then "Users", to see the list of accounts and when each one last signed in.',
    ],
    note: 'A steady or growing curve is normal. A sudden drop to zero or an unusual peak is worth reporting to your technical team.',
    images: [
      {
        src: '/guides/supabase/menu-observability.png',
        alt: 'Supabase project menu with Observability highlighted',
        caption: 'Step 4: "Observability" in the left menu of the project',
      },
      {
        src: '/guides/supabase/authentication-users.png',
        alt: 'Supabase Authentication menu with Users highlighted',
        caption: 'Step 5: "Users" inside the "Authentication" menu',
      },
    ],
  },
  {
    icon: HiOutlineCreditCard,
    title: 'Check your remaining quota and billing',
    intro:
      'Supabase does not work with prepaid credit. Your plan includes a monthly allowance, and you can see how much of it has been used.',
    steps: [
      'In the left menu, open "Project Settings" (at the bottom).',
      'A second, smaller menu appears on the left. Scroll to the bottom of it: the last group is called "Billing" and contains two links, "Subscription" and "Usage".',
      'Click "Usage" to see what has been consumed in the current billing period: database size, file storage, data transfer and monthly active users. Each item shows a bar comparing your usage with what your plan includes.',
      'Click "Subscription" to see your current plan, the upcoming invoice, your credit balance, your payment method and your past invoices.',
    ],
    note: 'The "Usage" and "Subscription" pages open the billing area of your organization, so the figures cover all the projects of the organization, not only Kleezy.',
    images: [
      {
        src: '/guides/supabase/menu-project-settings.png',
        alt: 'Supabase project menu with Project Settings highlighted at the bottom',
        caption: 'Step 1: "Project Settings" at the bottom of the left menu',
      },
      {
        src: '/guides/supabase/settings-billing-usage.png',
        alt: 'Supabase Project Settings menu with the Billing group showing Subscription and Usage',
        caption: 'Steps 2 to 4: the "Billing" group at the bottom of the settings menu',
      },
    ],
  },
  {
    icon: HiOutlineCreditCard,
    title: 'Free plan limits',
    intro:
      'The Kleezy project is currently on the Free plan. This plan is enough for a small usage, but it has limits to keep in mind.',
    steps: [
      'The project is paused automatically after about one week without any activity. It can be restored from the dashboard, but the platform is unavailable in the meantime.',
      'The free allowance is limited (database size, file storage, data transfer and number of active users). The "Usage" page shows how close you are to each limit.',
      'When a limit is exceeded, Supabase can restrict the project until the next billing period or until the plan is upgraded.',
      'Upgrading to a paid plan is done from the "Subscription" page and takes effect immediately.',
    ],
    note: 'If the platform suddenly becomes unavailable, check "Project Overview" first: a paused project shows a "Restore" button instead of the "Healthy" status.',
  },
  {
    icon: HiOutlineUserCircle,
    title: 'Check your account information',
    intro:
      'Review your personal account and who has access to the Kleezy project.',
    steps: [
      'Click your profile picture in the top-right corner and select "Account preferences" to see or update your name, email address and password.',
      'Click the organization name at the top of the page and open "Team" to see who has access and with which role.',
      'In the project, open "Project Settings", then "General", to see the project name and the region where your data is hosted.',
    ],
  },
]

const usedFor = [
  'Storing the data of the platform: agencies, properties, users and keys.',
  'Managing sign-ins, passwords and access rights of your users.',
  'Storing the files uploaded to the platform.',
]

const goodToKnow = [
  'You do not need to open Supabase for Kleezy to work day to day. Agencies and users are managed from this admin site.',
  'Please do not edit or delete anything in "Table Editor", "Authentication" or "Project Settings". A change made there applies immediately to the live platform and cannot always be undone.',
  'Never share the project API keys or the database password outside your technical team.',
  'Menu names in Supabase may change slightly over time. If something looks different, contact your technical team.',
]

const supabaseGuide = {
  id: 'supabase',
  label: 'Supabase',
  subtitle: 'The service that stores the data of your Kleezy platform',
  dashboardUrl: 'https://supabase.com/dashboard',
  dashboardLabel: 'Open Supabase',
  role: 'Supabase is the data layer of Kleezy. It is where everything you see in this admin site and in the app is saved and kept secure.',
  usedFor,
  sections,
  goodToKnow,
}

export default supabaseGuide
